import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Hook global de taxas da plataforma.
 * - Consome GET /api/public/fees (endpoint público).
 * - Faz refresh ao montar + a cada 15s (pooling leve).
 * - Também revalida quando a aba volta ao foco, garantindo que qualquer
 *   alteração feita no Admin apareça imediatamente pro cliente.
 */
export function useFeeRates() {
  const [rates, setRates] = useState({
    bank_fee_rate: 0,
    deposit_fee_rate: 0,
    transfer_fee_rate: 0,
    withdraw_fee_rate: 0,
    sales_commission_rate: 0,
    default_affiliate_rate: 0,
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/public/fees");
      // Backend pode responder sem alguns campos específicos —
      // caimos no bank_fee_rate como fallback.
      const base = Number(data?.bank_fee_rate ?? 0);
      setRates({
        bank_fee_rate: base,
        deposit_fee_rate: Number(data?.deposit_fee_rate ?? base),
        transfer_fee_rate: Number(data?.transfer_fee_rate ?? base),
        withdraw_fee_rate: Number(data?.withdraw_fee_rate ?? base),
        sales_commission_rate: Number(data?.sales_commission_rate ?? 0),
        default_affiliate_rate: Number(data?.default_affiliate_rate ?? 0),
      });
    } catch (e) {
      // silencioso — se der ruim, mantém últimos valores (ou zeros iniciais)
      // eslint-disable-next-line no-console
      console.warn("useFeeRates: falha ao buscar taxas", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  return { rates, loading, reload: load };
}

/**
 * Formata uma taxa (0.05) como percentual amigável (5%, 4,5%, etc).
 */
export function fmtPct(rate) {
  const n = Number(rate || 0) * 100;
  if (n === 0) return "0%";
  if (Number.isInteger(n)) return `${n}%`;
  return `${n.toFixed(2).replace(/\.?0+$/, "").replace(".", ",")}%`;
}
